import type { FormEvent } from 'react';
import { useEffect, useRef } from 'react';

import { Button } from '@/components/Button';
import type { NameRefusal } from '@/lib/enrolmentSession';
import { KIOSK_NAME_MAX_LENGTH, useEnrolmentSession } from '@/lib/enrolmentSession';

import {
  ENROLMENT_COPY,
  nameSuggestionHelper,
  nameTakenDirection,
  nameTakenMessage,
  RECORDED_COPY,
  RULED_COPY,
} from '../copy';
import { EnrolmentHeading } from './EnrolmentHeading';
import { EnrolmentShell } from './EnrolmentShell';
import { KioskNameField } from './KioskNameField';

const FIELD_ID = 'kiosk-name';
const DIRECTION_ID = `${FIELD_ID}-direction`;

/**
 * The naming step — frames *07 · Name this kiosk* and *08 · Name already taken*.
 *
 * **They are one screen in two states, which is how the file draws them**: the same heading block, the same
 * field, the same action, with the control in its error treatment and a message under it once the server has
 * refused the name. Nothing else differs — not the copy, not the layout, not the button.
 *
 * ⭐ **The staffer INVENTS the name** [AC-1b; [`0289`]]. Nothing here asks for, reads or checks a physical
 * tag, and nothing is auto-assigned, suggested or offered in a picker [0118; 0149].
 * ⛔ **And no *"label the tablet to match"* instruction is added.** A design annotation claims the screen
 * says so; ⛔ **no such string exists on any of the six naming frames**, verified live 2026-09-16 at 1440,
 * 820 and 375 — it is undrawn copy owned by Design, and it is not invented here.
 *
 * ⭐ **The G11 helper SHIPS, on the COLLISION state only** — *"Suggested from the kiosks already at
 * ‹venue›. Change it to something staff would recognise — “Front desk”, “Lobby”."* ⛔ **Both halves of
 * that are an Operator ruling of 2026-09-17, not a derivation**: the frames draw it visible in BOTH states,
 * and withholding it from the resting screen is a registered departure from *07*. The venue is a parameter
 * — the frames' *Bethlehem Office* is a sample exactly as *Front desk* is — and ⏳ **the sentence's first
 * clause is owed to Design**, because nothing suggests anything in either state [`nameSuggestionHelper`].
 *
 * ⛔ **One VISIBLE string the frames draw is NOT shipped**, and nothing is invented in its place:
 *
 *  - the note *"You can rename it later from the Admin."* — ⛔ **no story specs a rename and no admin frame
 *    draws one** (`FLOW — KIOSKS & DEVICES` has `Regenerate PIN` and `Remove device`), and [`0251`] rules
 *    that a name is freed only by deleting its record [AC-4c; KAD-6d § G12]. ⏳ **Owed until a rename is
 *    planned or implemented in the Admin** [Operator, 2026-09-17].
 *
 * ⏳ The gate stays OPEN. This screen omits the drawn copy; it does not resolve it, and the omission is
 * registered as a known departure for design QA.
 *
 * ⭐ **The admin removal direction SHIPS, on the collision only** [03-us-kad-6p]. KAD-6d AC-4 asked that a
 * refused name also direct the staffer to have an admin remove the old kiosk — under [`0251`] that removal
 * is the only thing that frees a name — and [`0376`] deferred it until Design drew it. Design has: the node
 * `Admin removal direction (0376)` under the field and its helper. It renders as drawn except one word,
 * *site* for the frame's *office* [`0439`; 0297] — `nameTakenDirection`.
 *
 * ⛔ **Withholding them does NOT make the slot defaults shippable, and the two slots render NOTHING.** The
 * library's own placeholders — `Helper text`, `Error text`, `Action link →` — are not a fallback for a
 * withheld string; they are the six strings that never reach a screen [[`0225`]], along with `*`, `Action`,
 * `R$`, `kg` and `0/240`.
 */
export function NameKioskScreen() {
  const name = useEnrolmentSession((state) => state.name);
  const nameRefusal = useEnrolmentSession((state) => state.nameRefusal);
  const venueName = useEnrolmentSession((state) => state.venueName);
  const namingInFlight = useEnrolmentSession((state) => state.namingInFlight);
  const typeName = useEnrolmentSession((state) => state.typeName);
  const submitName = useEnrolmentSession((state) => state.submitName);

  const field = useRef<HTMLInputElement>(null);

  // The collision ONLY [03-us-kad-6p AC-2]: the resting step, an empty field and an unreadable answer get none.
  const direction =
    nameRefusal?.kind === 'taken' ? nameTakenDirection(nameRefusal.name) : undefined;

  /*
   * ⭐ **A refusal puts the cursor back where the fix is** [AC-9]. The field is the only thing on this screen
   * a staffer can act on, and after tapping the action their focus is on the button — so without this a
   * keyboard user has to shift-tab back to a control they were just told is wrong. The message itself is
   * announced by its own live region, in `KioskNameField`.
   */
  useEffect(() => {
    if (nameRefusal !== undefined) field.current?.focus();
  }, [nameRefusal]);

  /*
   * A `<form>`, so Enter submits from the field without a keydown handler — the action is the only one on
   * the screen and that is what a staffer at a keyboard expects [AC-9].
   *
   * `noValidate` because the refusal is OURS: the browser's own bubble for a `required` field is not a live
   * region, cannot be styled to the frame, and would pre-empt the announced message with an unlocalised
   * sentence nobody chose.
   */
  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    void submitName();
  };

  return (
    <EnrolmentShell>
      <form
        className="flex w-full flex-col items-center gap-6 pb-8 pt-2"
        noValidate
        onSubmit={onSubmit}
      >
        <EnrolmentHeading
          body={ENROLMENT_COPY.nameBody}
          eyebrow={ENROLMENT_COPY.eyebrow}
          heading={ENROLMENT_COPY.nameHeading}
        />

        <KioskNameField
          // The direction reaches a screen reader the way the helper does — from the field it is about.
          describedBy={direction === undefined ? undefined : DIRECTION_ID}
          error={nameRefusal === undefined ? undefined : messageFor(nameRefusal)}
          /*
            ⭐ **The collision state ONLY** [Operator ruling, 2026-09-17]. Not the resting screen, where the
            staffer has been given no name to change, and not the other two refusals: `required` is an empty
            field and `unreadable` is the server, and neither is the duplicate-name state the ruling names.
          */
          helper={nameRefusal?.kind === 'taken' ? nameSuggestionHelper(venueName) : undefined}
          id={FIELD_ID}
          inputRef={field}
          label={ENROLMENT_COPY.nameLabel}
          maxLength={KIOSK_NAME_MAX_LENGTH}
          onChange={typeName}
          placeholder={ENROLMENT_COPY.namePlaceholder}
          value={name}
        />

        {direction !== undefined && (
          /*
            ⭐ **A sibling of the field, not part of it** — `Admin removal direction (0376)` is a child of the
            frame's `Content`, 24px under `Field/Kiosk name` (the form's `gap-6`), on the field's left edge
            rather than the helper's 20px indent. SF Pro Text 400 13/16 in #585756 (`--muted-foreground`) at
            all three widths [Figma, read live 2026-10-08]; it wraps at 820 and 375.
            Not a live region: the alert above already says what happened, and this is what to do about it.
          */
          <p
            className="w-full text-left text-[13px] leading-4 text-[var(--muted-foreground)]"
            id={DIRECTION_ID}
          >
            {direction}
          </p>
        )}

        <Button disabled={namingInFlight} fullWidth type="submit">
          {ENROLMENT_COPY.nameAction}
        </Button>
      </form>
    </EnrolmentShell>
  );
}

/**
 * What the field says, for each of the three ways a name can be refused.
 *
 * ⭐ **Only the first is drawn.** The collision sentence is the design's, with the refused name in it
 * [AC-3]; the empty-field sentence is this story's, recorded rather than left to a silent disabled button
 * [`RECORDED_COPY`]; and an answer the tablet cannot read reuses the sentence the flow already has for
 * exactly that, rather than inventing a second one [`RULED_COPY.havingIssues`; 0262's *nothing disclosed*].
 */
function messageFor(refusal: NameRefusal): string {
  if (refusal.kind === 'taken') return nameTakenMessage(refusal.name);
  if (refusal.kind === 'required') return RECORDED_COPY.nameRequired;
  return RULED_COPY.havingIssues;
}
